<template lang="pug">
section.loading-dots(v-if="patientDetailsLoading")
  .dot1
  .dot2
  .dot3
section.patient-detail-section(v-else)
  transition(
    name="fade"
    v-on:before-enter="blockBodyScroll"
    v-on:after-leave="unblockBodyScroll")
    section.coaching-chat-modal(
      v-if="isCoach && userHasCoaching && expandedCoachingChatShown"
      @click.self="hideExpandedCoachingChat")
      .modal-content(
        @click.self="hideExpandedCoachingChat"
        @keyup.esc="hideExpandedCoachingChat")
        coaching-chat(
          :user-id="id"
          :patient="patient"
          :isExpandedView="true"
          :isSupervisorView="isSupervisorView"
          v-on:hide-expanded-coach-chat="hideExpandedCoachingChat"
          v-on:coaching-messages-loaded="coachingMessagesLoadedEvent")
  ul.patient-header
    li.name
      h3 {{ firstName }}
    li.email
      p {{ patient.emailAddress }}
    li.access-code
      p {{ patient.accessCode }}
    li.engagement
      assessment-icon(:assessment="assessment" v-for="(assessment, index) in latestScores(patient)" :key="index")
      dl.interaction
        dt Recent Interaction
        dd {{ lastInteractionDt(patient) | shortDate }} {{ lastInteraction(patient) }}
      p.review-btn(v-if="!isCoachView && !isClinicalPortal")
        button.btn.small(@click="markPatientAsReviewed(id)") Mark as Reviewed
        small(v-if="reviewedRecently") Reviewed {{ patient.lastReviewed | shortDate }}
        small(v-else) Not reviewed in last day
      //- Coaching supervisor review & manual crisis
      dl.coaching-supervisor-review-status(v-if="isCoachView && isSupervisorView")
        dt Last supervisor review:&nbsp;
        dd
          template(v-if="coachingMessages.supervisorReviewDate === null")
            | Not reviewed
          template(v-else)
            | {{ coachingMessages.supervisorReviewDate | longDate }}
      p.coaching-status(v-if="isCoachView")
        span.supervisor-review-wrapper(v-if="isSupervisorView")
          button.btn.small.success(@click="doSetPatientSupervisorReview(patient.id)")
            | Mark patient as reviewed
        span.crisis-button-wrapper(v-if="canEnableReview")
          button.btn.small(
            v-if="patientInCrisis(patient.crisisStatus)"
            @click="disableCoachingCrisis") Disable Review
          button.btn.small(
            v-if="!patientInCrisis(patient.crisisStatus)"
            @click="enableCoachingCrisis") Enable Review
        small.review-label(v-show="patientInCrisis(patient.crisisStatus)")
          | Needs review:&nbsp;
          span.bold {{ patient.crisisStatus }}

  .patient-detail-content
    nav.tabs
      ul
        //- Removed
        //- li
        //-   a(href="#" v-bind:class="{active:tabs.showProgram}" @click="setActive('showProgram')") Program Progress
        li
          a(
            href="#"
            v-bind:class="{active:tabs.showProfile}"
            v-on:click.prevent="setActive('showProfile')")
            | Profile
        li
          a(
            href="#"
            v-bind:class="{active:tabs.showAssessment}"
            v-on:click.prevent="setActive('showAssessment')")
            | Assessment Scores
        li
          a(
            href="#"
            v-bind:class="{active:tabs.showActivity}"
            v-on:click.prevent="setActive('showActivity')")
            | Activity History
        li(v-if="showClinicalCoaching")
          a(
            href="#"
            v-bind:class="{active:tabs.showClinicalCoaching}"
            v-on:click.prevent="setActive('showClinicalCoaching')")
            | Coaching sessions
        li
          a(
            href="#"
            v-bind:class="{active:tabs.showTracker}"
            v-on:click.prevent="setActive('showTracker')")
            | Tracker History
        li
          a(
            href="#"
            v-bind:class="{active:tabs.showHelp}"
            v-on:click.prevent="setActive('showHelp')")
            | Help Resources

    section.tab-content
      //- Removed:
      //- patient-program(v-if="tabs.showProgram")
      patient-profile(
        v-if="tabs.showProfile"
        :patient="patient")
      patient-assessment(
        v-if="tabs.showAssessment"
        :assessments="patient.assessmentScores")
      patient-activity(
        v-if="tabs.showActivity"
        :activities="patientActivities")
      patient-clinical-coaching(
        v-if="tabs.showClinicalCoaching"
        :showPermissionError="!showClinicalCoaching"
        :patientId="patient.id")
      patient-tracker(
        v-if="tabs.showTracker"
        :trackerHistory="patient.trackerHistory")
      help-resources(
        v-if="tabs.showHelp"
        :accessCode="patient.accessCode"
        :orgName="patient.orgShortName"
        :telehealthProvider="patient.telehealthProvider")

    coaching-chat(
      v-if="showCoachingChat"
      :user-id="id"
      :patient="patient"
      :updatedOn="coachingChatUpdate"
      :isExpandedView="false"
      :isSupervisorView="isSupervisorView"
      v-on:show-expanded-coach-chat="showExpandedCoachingChat"
      v-on:coaching-messages-loaded="coachingMessagesLoadedEvent")
</template>

<script>
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';

import auth from '@/services/authSvc.js';

import PatientClinicalCoaching from '@/components/patient/detail/PatientClinicalCoaching.vue';
import PatientAssessment from '@/components/patient/detail/PatientAssessment.vue';
import PatientActivity from '@/components/patient/detail/PatientActivity.vue';
import PatientProfile from '@/components/patient/detail/PatientProfile.vue';
import PatientProgram from '@/components/patient/detail/PatientProgram.vue';
import PatientTracker from '@/components/patient/detail/PatientTracker.vue';
import HelpResources from '@/components/patient/detail/HelpResources.vue';
import CoachingChat from '@/components/coaching/CoachingChat.vue';

import lastInteractionMixin from '@/mixins/lastInteraction.js';
import latestScoresMixin from '@/mixins/latestScores.js';
import AssessmentIcon from './patient/AssessmentIcon';

export default {
  name: 'Patient',
  mixins: [lastInteractionMixin, latestScoresMixin],
  // Prop id passed from route
  props: ['id'],
  components: {
    PatientClinicalCoaching,
    PatientAssessment,
    PatientActivity,
    PatientProfile,
    PatientProgram,
    PatientTracker,
    AssessmentIcon,
    CoachingChat,
    HelpResources
  },
  data() {
    return {
      userHasCoaching: false,
      coachingChatUpdate: new Date().getTime(),
      canEnableReview: false,
      tabs: {
        showHelp: false,
        showProfile: true,
        showProgram: false,
        showTracker: false,
        showActivity: false,
        showAssessment: false,
        showClinicalCoaching: false
      },
      isClinicalPortal: false,
      // For expanded coaching chat UI
      expandedCoachingChatShown: false,
      // Query param driven
      isCoachView: false,
      isSupervisorView: false
    };
  },
  mounted() {
    this.init();
  },
  computed: {
    showCoachingChat() {
      return (
        this.isCoach && this.userHasCoaching && !this.tabs.showClinicalCoaching
      );
    },
    showClinicalCoaching() {
      const clinicalCoachingFeatureFlag =
        this.apiConfig.platforms &&
        this.apiConfig.platforms.web.features.clinical_coaching;
      return this.isClinicalPortal && clinicalCoachingFeatureFlag;
    },
    reviewedRecently() {
      if (this.patient && this.patient.lastReviewed) {
        const date = new Date(this.patient.lastReviewed);
        const reviewDt = moment(date);
        const yesterday = moment().subtract(1, 'days');
        return reviewDt.isAfter(yesterday);
      }
      return false;
    },
    patient() {
      const patientDetails = this.getPatientDetailsById(this.id);
      return this.id && patientDetails ? patientDetails : {};
    },
    firstName() {
      return this.patient ? this.patient.firstName : '';
    },
    ...mapGetters([
      'getPatientDetailsById',
      'patientDetailsLoading',
      'patientActivities',
      'isCoachSupervisor',
      'patientNotFound',
      'coachingMessages',
      'isCoach',
      'user',
      'apiConfig'
    ])
  },
  methods: {
    init() {
      this.setQueryParamData();
      this.getPatientDetails(this.id).then(() => {
        // Enable coaching chat if user is enrolled in coaching
        if (this.patient && this.patient.coachingUser) {
          this.userHasCoaching = true;
        }

        if (this.patientNotFound) {
          this.$router.push({ name: 'NotFound' });
        } else {
          if (this.id) {
            this.getPatientActivityHistory(this.id);
          }
        }
      });
      this.isClinicalPortal =
        auth.isClinicalPortal() || auth.isCoachSupervisor();

      // Show tab specified by activeTab query parameter
      this.setActiveTabFromQueryParam();
    },
    setActiveTabFromQueryParam() {
      const activeTab = this.$route.query.activeTab || null;
      if (!activeTab) {
        return;
      }

      const tabs = this.tabs;
      Object.keys(tabs).forEach(key => {
        tabs[key] = key === activeTab;
      });
    },
    setActive(tab) {
      const tabs = this.tabs;
      Object.keys(tabs).forEach(key => {
        tabs[key] = key === tab;
      });
      // Prevent duplicate navigation exceptions
      if (this.$route.query && this.$route.query.activeTab !== tab) {
        this.$router.push({ query: { ...this.$route.query, activeTab: tab } });
      }
    },
    coachingMessagesLoadedEvent(coachData) {
      if (this.user.id === coachData.coachUserId || this.isCoachSupervisor) {
        this.canEnableReview = true;
      }
    },
    patientInCrisis(status) {
      return status !== 'NONE';
    },
    enableCoachingCrisis() {
      this.setUserCrisisStatus({
        userId: this.id,
        enableCrisis: true
      }).then(() => {
        // Update patient data
        this.getPatientDetails(this.id);
      });
    },
    disableCoachingCrisis() {
      this.setUserCrisisStatus({
        userId: this.id,
        enableCrisis: false
      }).then(() => {
        // Update patient data
        this.getPatientDetails(this.id);
      });
    },
    showExpandedCoachingChat() {
      this.expandedCoachingChatShown = true;
    },
    hideExpandedCoachingChat() {
      this.expandedCoachingChatShown = false;
      this.coachingChatUpdate = new Date().getTime();
    },
    blockBodyScroll() {
      this.$root.$emit('block-body-scroll');
    },
    unblockBodyScroll() {
      this.$root.$emit('unblock-body-scroll');
    },
    doSetPatientSupervisorReview() {
      this.setPatientSupervisorReview(this.id).then(() => {
        this.getPatientDetails(this.id);
      });
    },
    setQueryParamData() {
      const queryCoaching = this.$route.query.coaching;
      const querySupervisor = this.$route.query.supervisor;

      // Checking string true OR boolean due to inconsistencies in vue router
      // Reload causes string true & navigation route sets boolean
      this.isCoachView = queryCoaching === 'true' || queryCoaching === true;
      this.isSupervisorView =
        querySupervisor === 'true' || querySupervisor === true;
    },
    ...mapActions([
      'getPatientActivityHistory',
      'setPatientSupervisorReview',
      'markPatientAsReviewed',
      'setUserCrisisStatus',
      'getPatientDetails'
    ])
  }
};
</script>

<style lang="scss" scoped>
.coaching-chat-modal {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1050;
  overflow-x: hidden;
  overflow-y: scroll;
  outline: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 1;

  &.fade-enter-active,
  &.fade-leave-active {
    transition: opacity 0.33s;
  }
  &.fade-enter,
  &.fade-leave-to {
    opacity: 0;
  }

  .modal-content {
    max-width: 1080px;
    margin: 0 auto;
    height: 100%;

    .coaching-chat-wrapper {
      height: auto;
      max-height: none;
      overflow: hidden;
    }
  }
}
.patient-header {
  width: 100%;
  margin: 0 auto;
  padding: $small-spacing;
  background: #fff;
  border-bottom: 1px solid $md-grey-200;
  li {
    display: inline-block;
    padding-bottom: $xs-spacing;
    padding-right: $base-spacing;
    h3,
    p {
      @include zero;
    }
  }
  li.engagement {
    display: block;
    padding: 0;
    padding-top: $small-spacing;
    border-top: 1px solid $black;
  }
  .name {
    h3 {
      line-height: 1;
      font-size: $ms-up2;
    }
  }
}
.interaction,
.coaching-supervisor-review-status {
  display: inline-block;
  @include zero;
  text-align: center;
  padding: 0 $small-spacing;

  dt,
  dd {
    @include zero;
  }
  dt {
    line-height: 1;
    text-transform: uppercase;
    font-size: $ms-down1;
  }
}
.review-btn {
  @include zero;
  display: inline-block;
  vertical-align: top;

  .btn {
    margin-left: $base-spacing;
  }
  small {
    margin-left: $base-spacing;
  }
}
li p.coaching-status {
  display: inline-block;
  vertical-align: top;
  height: 38px;
  color: #d32f2f;
  margin: 0 $base-spacing;

  .bold {
    font-weight: bold;
  }
  .supervisor-review-wrapper {
    margin: 0;
  }
  .crisis-button-wrapper {
    margin: 0 0 0 15px;
  }
  .review-label {
    margin: 0 15px;
  }
}
.patient-detail-content {
  margin: 0 auto;
  background: #fff;
  display: flex;
  justify-content: space-apart;
  align-content: stretch;
}
.tabs {
  // width: 20%;
  flex: 0 0 20%;
  min-width: 12em;
  border-right: 1px solid $md-grey-200;
  ul {
    padding-bottom: $small-spacing;
    li {
      display: block;
      text-transform: uppercase;
      line-height: 1.2;
    }
    a {
      display: block;
      position: relative;
      padding: $small-spacing;
      border-bottom: 1px solid $md-grey-200;
      color: $grey;
      &:hover,
      &:focus {
        color: $black;
      }
    }
    a.active {
      color: $action-color;
      font-weight: $base-font-weight-strong;
      &:after {
        content: '';
        position: absolute;
        display: block;
        top: 50%;
        margin-top: -6px;
        left: -6px;
        @include size(12px);
        background: $action-color;
        border-radius: 12px;
      }
    }
  }
}
.tab-content {
  padding: $small-spacing;
  flex: 1 1 auto;
  min-width: 50%;
  overflow-y: auto;
  max-height: 90vh;
}
</style>
